import { useEffect, useMemo, useState, useCallback } from "react";
import { fetchAPI, getToday } from "../lib/utils";
import {
	competitionSportOptions,
	matchesCompetitionSport,
} from "../lib/competitionSports";
import { CompetitionSportField } from "./sports/CompetitionSportField";
import { getImageUrl, matchesCoachAthleteSearch } from "../lib/coachUtils";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import {
	CompetitionParticipantsDialog,
	SharedAthletesBadge,
} from "./planning/CompetitionParticipantsDialog";
import {
	CalendarDays,
	CalendarRange,
	Trophy,
	MapPin,
	Users,
	Search,
	Filter,
	Loader2,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	LayoutGrid,
	Flame,
	Plus,
	Pencil,
	Trash2,
	UserPlus,
	Download,
	AlertTriangle,
	Check,
	Info,
	X,
} from "lucide-react";

const EVENT_TYPES = ["gara", "qualificazione", "open", "test", "altro"];

const EVENT_TYPE_STYLES = {
	gara: "bg-blaze/20 text-blaze",
	qualificazione: "bg-acid/20 text-acid",
	open: "bg-volt/20 text-volt",
	test: "bg-muted text-muted-foreground",
	altro: "bg-muted text-muted-foreground",
};

const eventTypeClass = (type) =>
	EVENT_TYPE_STYLES[type] || "bg-muted text-muted-foreground";

const PRIORITIES = ["A", "B", "C"];

const PRIORITY_STYLES = {
	A: "bg-blaze/20 text-blaze",
	B: "bg-volt/20 text-volt",
	C: "bg-muted text-muted-foreground",
};

const priorityClass = (priority) =>
	PRIORITY_STYLES[priority] || "bg-muted text-muted-foreground";

const STATUSES = ["planned", "confirmed", "registered", "done", "cancelled"];

const STATUS_LABEL_KEYS = {
	planned: "competitionStatusPlanned",
	confirmed: "competitionStatusConfirmed",
	registered: "competitionStatusRegistered",
	done: "competitionStatusDone",
	cancelled: "competitionStatusCancelled",
};

const initials = (name) =>
	(name || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join("");

const monthLabel = (yyyymm) => {
	const [year, month] = yyyymm.split("-");
	const date = new Date(Number(year), Number(month) - 1, 1);
	return date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
};

const shortDate = (iso) => {
	if (!iso) return "—";
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return iso;
	return date.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
};

const daysUntil = (iso, today) => {
	const target = new Date(iso);
	const base = new Date(today);
	if (Number.isNaN(target.getTime())) return null;
	return Math.round((target - base) / (1000 * 60 * 60 * 24));
};

const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const pad2 = (value) => String(value).padStart(2, "0");

const ymKey = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;

const isoFromYmd = (year, month, day) =>
	`${year}-${pad2(month + 1)}-${pad2(day)}`;

// Monday-based weekday index (0 = Monday ... 6 = Sunday).
const mondayIndex = (jsDay) => (jsDay + 6) % 7;

const monthsBetween = (startKey, endKey) => {
	if (!startKey || !endKey) return [];
	const [sy, sm] = startKey.split("-").map(Number);
	const [ey, em] = endKey.split("-").map(Number);
	const result = [];
	let year = sy;
	let month = sm;
	while (year < ey || (year === ey && month <= em)) {
		result.push(`${year}-${pad2(month)}`);
		month += 1;
		if (month > 12) {
			month = 1;
			year += 1;
		}
	}
	return result;
};

const monthShortLabel = (yyyymm) => {
	const [year, month] = yyyymm.split("-");
	const date = new Date(Number(year), Number(month) - 1, 1);
	return date.toLocaleDateString("it-IT", { month: "short", year: "2-digit" });
};

const EMPTY_FORM = {
	name: "",
	date: "",
	end_date: "",
	event_type: "gara",
	discipline: "",
	sport: null,
	priority: "",
	category: "",
	status: "",
	location: "",
	notes: "",
};

// Priority-A races closer than this many days for the same athlete raise a conflict.
const CONFLICT_WINDOW_DAYS = 21;

const csvCell = (value) => {
	const text = value == null ? "" : String(value);
	if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
	return text;
};

const downloadBlob = (content, filename, mime) => {
	const blob = new Blob([content], { type: mime });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

export function CompetitionHubTab() {
	const { t } = useLanguage();
	const { user } = useAuth();
	const isAdmin = user?.is_admin || user?.role === "admin";
	const today = getToday();

	const [loading, setLoading] = useState(true);
	const [events, setEvents] = useState([]);
	const [athletes, setAthletes] = useState([]);
	const [scope, setScope] = useState("mine");

	const [search, setSearch] = useState("");
	const [period, setPeriod] = useState("upcoming");
	const [selectedTypes, setSelectedTypes] = useState([]);
	const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);
	const [selectedPriorities, setSelectedPriorities] = useState([]);
	const [selectedDiscipline, setSelectedDiscipline] = useState("__all__");
	// Gli sport del catalogo, per le etichette del filtro (D16 di docs/sport-config).
	const [sportsCatalog, setSportsCatalog] = useState([]);
	const [selectedStatus, setSelectedStatus] = useState("__all__");
	const [sharedOnly, setSharedOnly] = useState(false);
	const [athletePickerOpen, setAthletePickerOpen] = useState(false);
	const [athleteSearch, setAthleteSearch] = useState("");
	const [view, setView] = useState("list");
	const [calendarMonth, setCalendarMonth] = useState(() => {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), 1);
	});
	const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

	// Who else is attending a given race (opened from the "N athletes" pill)
	const [participantsEvent, setParticipantsEvent] = useState(null);
	const [participants, setParticipants] = useState([]);
	const [participantsLoading, setParticipantsLoading] = useState(false);

	// Actions (Fase 4)
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState("create");
	const [editingEventId, setEditingEventId] = useState(null);
	const [form, setForm] = useState(EMPTY_FORM);
	const [formAthleteIds, setFormAthleteIds] = useState([]);
	const [saving, setSaving] = useState(false);
	const [selectedEventIds, setSelectedEventIds] = useState([]);
	const [bulkStatus, setBulkStatus] = useState("");
	const [dialogAthleteSearch, setDialogAthleteSearch] = useState("");
	// Athletes already attending the race targeted by the dialog (same
	// shared_group_key): used to lock them in the picker and to decide whether
	// the "apply to everyone" option makes sense on edit.
	const [groupMemberIds, setGroupMemberIds] = useState([]);
	const [applyToGroup, setApplyToGroup] = useState(false);
	// Athlete groups (the coach's own, not the derived "same race" clusters
	// that `groupMemberIds` refers to): one tap assigns a race to a whole group.
	const [athleteGroups, setAthleteGroups] = useState([]);

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const effectiveScope = isAdmin ? scope : "mine";
			const data = await fetchAPI(
				`/coach/competitions?scope=${effectiveScope}`,
			);
			setEvents(Array.isArray(data?.events) ? data.events : []);
			setAthletes(Array.isArray(data?.athletes) ? data.athletes : []);
			// I gruppi seguono la stessa maschera degli atleti: con `scope=mine`
			// (il default anche per l'admin) sono i propri, "tutti" li allarga.
			fetchAPI(`/groups?scope=${effectiveScope}`)
				.then((groups) => setAthleteGroups(Array.isArray(groups) ? groups : []))
				.catch(() => setAthleteGroups([]));
		} catch {
			toast.error(t("competitionHubLoadError"));
			setEvents([]);
			setAthletes([]);
		} finally {
			setLoading(false);
		}
	}, [t, isAdmin, scope]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const toggleType = (type) => {
		setSelectedTypes((prev) =>
			prev.includes(type)
				? prev.filter((value) => value !== type)
				: [...prev, type],
		);
	};

	const toggleAthlete = (id) => {
		setSelectedAthleteIds((prev) =>
			prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
		);
	};

	const togglePriority = (priority) => {
		setSelectedPriorities((prev) =>
			prev.includes(priority)
				? prev.filter((value) => value !== priority)
				: [...prev, priority],
		);
	};

	useEffect(() => {
		let cancelled = false;
		fetchAPI("/sports")
			.then((data) => {
				if (!cancelled) setSportsCatalog(Array.isArray(data) ? data : []);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	// Il filtro per sport: gli sport collegati, poi i testi delle gare non
	// collegate a uno sport del catalogo (D16 di docs/sport-config).
	const disciplineOptions = useMemo(
		() => competitionSportOptions(events, sportsCatalog),
		[events, sportsCatalog],
	);

	// Count how many athletes attend each shared event (name|date|city cluster).
	const sharedCounts = useMemo(() => {
		const counts = new Map();
		events.forEach((event) => {
			const key = event.shared_group_key;
			counts.set(key, (counts.get(key) || 0) + 1);
		});
		return counts;
	}, [events]);

	// Base filters (everything except period) — used by calendar/timeline/KPIs
	// so month navigation stays period-independent.
	const scopedEvents = useMemo(() => {
		const term = search.trim().toLowerCase();
		return events.filter((event) => {
			if (
				selectedTypes.length > 0 &&
				!selectedTypes.includes(event.event_type || "altro")
			) {
				return false;
			}
			if (
				selectedAthleteIds.length > 0 &&
				!selectedAthleteIds.includes(event.athlete_id)
			) {
				return false;
			}
			if (sharedOnly && (sharedCounts.get(event.shared_group_key) || 0) < 2) {
				return false;
			}
			if (
				selectedPriorities.length > 0 &&
				!selectedPriorities.includes(event.priority)
			) {
				return false;
			}
			if (!matchesCompetitionSport(event, selectedDiscipline)) {
				return false;
			}
			if (selectedStatus !== "__all__" && event.status !== selectedStatus) {
				return false;
			}
			if (term) {
				const haystack = `${event.name || ""} ${event.location || ""} ${
					event.athlete_name || ""
				}`.toLowerCase();
				if (!haystack.includes(term)) return false;
			}
			return true;
		});
	}, [
		events,
		search,
		selectedTypes,
		selectedAthleteIds,
		sharedOnly,
		sharedCounts,
		selectedPriorities,
		selectedDiscipline,
		selectedStatus,
	]);

	const filteredEvents = useMemo(() => {
		return scopedEvents.filter((event) => {
			if (period === "upcoming" && event.date < today) return false;
			if (period === "past" && event.date >= today) return false;
			if (period === "next30" || period === "next90") {
				const delta = daysUntil(event.date, today);
				if (delta === null || delta < 0) return false;
				if (period === "next30" && delta > 30) return false;
				if (period === "next90" && delta > 90) return false;
			}
			return true;
		});
	}, [scopedEvents, period, today]);

	const eventsByMonth = useMemo(() => {
		const groups = new Map();
		filteredEvents.forEach((event) => {
			const key = (event.date || "").slice(0, 7) || "—";
			if (!groups.has(key)) groups.set(key, []);
			groups.get(key).push(event);
		});
		return Array.from(groups.entries())
			.sort((a, b) => a[0].localeCompare(b[0]))
			.map(([month, list]) => [
				month,
				list.sort((a, b) => (a.date || "").localeCompare(b.date || "")),
			]);
	}, [filteredEvents]);

	const sharedGroups = useMemo(() => {
		const groups = new Map();
		filteredEvents.forEach((event) => {
			const key = event.shared_group_key;
			if (!groups.has(key)) {
				groups.set(key, {
					key,
					name: event.name,
					date: event.date,
					location: event.location,
					event_type: event.event_type,
					athletes: [],
				});
			}
			groups.get(key).athletes.push(event);
		});
		return Array.from(groups.values()).sort((a, b) =>
			(a.date || "").localeCompare(b.date || ""),
		);
	}, [filteredEvents]);

	// KPI dashboard — computed on scopedEvents (period-independent).
	const kpis = useMemo(() => {
		const upcoming = scopedEvents
			.filter((event) => event.date >= today)
			.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
		const next30 = upcoming.filter((event) => {
			const delta = daysUntil(event.date, today);
			return delta !== null && delta >= 0 && delta <= 30;
		});
		const sharedUpcomingKeys = new Set();
		const sharedSeen = new Map();
		upcoming.forEach((event) => {
			sharedSeen.set(
				event.shared_group_key,
				(sharedSeen.get(event.shared_group_key) || 0) + 1,
			);
		});
		sharedSeen.forEach((count, key) => {
			if (count >= 2) sharedUpcomingKeys.add(key);
		});
		const athletesWithUpcoming = new Set(
			upcoming.map((event) => event.athlete_id),
		);
		const nextRace = upcoming[0] || null;
		return {
			next30: next30.length,
			sharedCount: sharedUpcomingKeys.size,
			withoutRace: Math.max(athletes.length - athletesWithUpcoming.size, 0),
			nextRace,
			nextRaceDelta: nextRace ? daysUntil(nextRace.date, today) : null,
		};
	}, [scopedEvents, athletes, today]);

	// Calendar month grid (scopedEvents, period-independent).
	const calendarGrid = useMemo(() => {
		const year = calendarMonth.getFullYear();
		const month = calendarMonth.getMonth();
		const firstWeekday = mondayIndex(new Date(year, month, 1).getDay());
		const daysInMonth = new Date(year, month + 1, 0).getDate();

		const byDay = new Map();
		scopedEvents.forEach((event) => {
			if ((event.date || "").slice(0, 7) === ymKey(calendarMonth)) {
				const dayNum = Number((event.date || "").slice(8, 10));
				if (!byDay.has(dayNum)) byDay.set(dayNum, []);
				byDay.get(dayNum).push(event);
			}
		});

		const cells = [];
		for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
		for (let day = 1; day <= daysInMonth; day += 1) {
			cells.push({
				day,
				iso: isoFromYmd(year, month, day),
				events: byDay.get(day) || [],
			});
		}
		return cells;
	}, [calendarMonth, scopedEvents]);

	const selectedDayEvents = useMemo(() => {
		if (!selectedCalendarDay) return [];
		return scopedEvents
			.filter((event) => event.date === selectedCalendarDay)
			.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
	}, [selectedCalendarDay, scopedEvents]);

	// Timeline swimlanes (one per athlete with events in scope).
	const timeline = useMemo(() => {
		const dated = scopedEvents.filter((event) => event.date);
		if (dated.length === 0) return { months: [], lanes: [] };
		const keys = dated.map((event) => event.date.slice(0, 7)).sort();
		const months = monthsBetween(keys[0], keys[keys.length - 1]).slice(0, 24);
		const monthSet = new Set(months);

		const laneMap = new Map();
		dated.forEach((event) => {
			const monthKey = event.date.slice(0, 7);
			if (!monthSet.has(monthKey)) return;
			if (!laneMap.has(event.athlete_id)) {
				laneMap.set(event.athlete_id, {
					athlete_id: event.athlete_id,
					name: event.athlete_name,
					picture: event.athlete_picture,
					byMonth: new Map(),
				});
			}
			const lane = laneMap.get(event.athlete_id);
			if (!lane.byMonth.has(monthKey)) lane.byMonth.set(monthKey, []);
			lane.byMonth.get(monthKey).push(event);
		});

		const lanes = Array.from(laneMap.values()).sort((a, b) =>
			(a.name || "").localeCompare(b.name || ""),
		);
		return { months, lanes };
	}, [scopedEvents]);

	const activeFilterCount =
		selectedTypes.length +
		selectedAthleteIds.length +
		selectedPriorities.length +
		(selectedDiscipline !== "__all__" ? 1 : 0) +
		(selectedStatus !== "__all__" ? 1 : 0) +
		(sharedOnly ? 1 : 0) +
		(period !== "upcoming" ? 1 : 0) +
		(search.trim() ? 1 : 0);

	const resetFilters = () => {
		setSearch("");
		setPeriod("upcoming");
		setSelectedTypes([]);
		setSelectedAthleteIds([]);
		setSelectedPriorities([]);
		setSelectedDiscipline("__all__");
		setSelectedStatus("__all__");
		setSharedOnly(false);
	};

	const shiftMonth = (delta) => {
		setSelectedCalendarDay(null);
		setCalendarMonth(
			(prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1),
		);
	};

	// Gap/conflict detection: same athlete with two priority-A upcoming races
	// closer than CONFLICT_WINDOW_DAYS.
	const conflicts = useMemo(() => {
		const byAthlete = new Map();
		events
			.filter((event) => event.priority === "A" && event.date >= today)
			.forEach((event) => {
				if (!byAthlete.has(event.athlete_id))
					byAthlete.set(event.athlete_id, []);
				byAthlete.get(event.athlete_id).push(event);
			});
		const result = [];
		byAthlete.forEach((list) => {
			const sorted = list.sort((a, b) => a.date.localeCompare(b.date));
			for (let i = 1; i < sorted.length; i += 1) {
				const prev = sorted[i - 1];
				const curr = sorted[i];
				const gap = daysUntil(curr.date, prev.date);
				if (gap !== null && gap <= CONFLICT_WINDOW_DAYS) {
					result.push({
						athlete_name: curr.athlete_name,
						a: prev,
						b: curr,
						gap,
					});
				}
			}
		});
		return result;
	}, [events, today]);

	// Every loaded event attending the same race as `event`.
	const groupMembersOf = useCallback(
		(event) =>
			events.filter(
				(candidate) => candidate.shared_group_key === event.shared_group_key,
			),
		[events],
	);

	const formFromEvent = (event) => ({
		name: event.name || "",
		date: event.date || "",
		end_date: event.end_date || "",
		event_type: event.event_type || "gara",
		discipline: event.discipline || "",
		sport: event.sport || null,
		priority: event.priority || "",
		category: event.category || "",
		status: event.status || "",
		location: event.location || "",
		notes: event.notes || "",
	});

	const openCreateDialog = () => {
		setDialogMode("create");
		setEditingEventId(null);
		setForm({ ...EMPTY_FORM, date: today });
		setFormAthleteIds([]);
		setGroupMemberIds([]);
		setApplyToGroup(false);
		setDialogAthleteSearch("");
		setDialogOpen(true);
	};

	const openEditDialog = (event) => {
		setDialogMode("edit");
		setEditingEventId(event.event_id);
		setForm(formFromEvent(event));
		setFormAthleteIds([event.athlete_id]);
		setGroupMemberIds(groupMembersOf(event).map((item) => item.athlete_id));
		setApplyToGroup(false);
		setDialogOpen(true);
	};

	// Add more athletes to a race that is already in the calendar: the race
	// fields are read-only here, only the athlete selection is editable.
	const openAddAthletesDialog = (event) => {
		setDialogMode("addAthletes");
		setEditingEventId(event.event_id);
		setForm(formFromEvent(event));
		setFormAthleteIds([]);
		setGroupMemberIds(groupMembersOf(event).map((item) => item.athlete_id));
		setApplyToGroup(false);
		setDialogAthleteSearch("");
		setDialogOpen(true);
	};

	// Races already on the calendar matching what the coach is typing, so the
	// same event is not created twice under a slightly different name.
	const nameSuggestions = useMemo(() => {
		if (dialogMode !== "create") return [];
		const term = form.name.trim().toLowerCase();
		if (term.length < 2) return [];
		const groups = new Map();
		events.forEach((event) => {
			if (!(event.name || "").toLowerCase().includes(term)) return;
			const key = event.shared_group_key;
			if (!groups.has(key)) groups.set(key, { key, anchor: event, count: 0 });
			groups.get(key).count += 1;
		});
		return Array.from(groups.values())
			.sort((a, b) => (a.anchor.date || "").localeCompare(b.anchor.date || ""))
			.slice(0, 5);
	}, [dialogMode, form.name, events]);

	const toggleFormAthlete = (id) => {
		setFormAthleteIds((prev) =>
			prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
		);
	};

	// Whole-group toggle: adds every visible member; a second tap on a fully
	// selected group removes them again. Members the coach cannot see (not in
	// `athletes`) are skipped, never silently enrolled.
	const toggleFormGroup = (group) => {
		const visibleIds = new Set(athletes.map((athlete) => athlete.user_id));
		const memberIds = (group.athlete_ids || []).filter((id) => visibleIds.has(id));
		if (memberIds.length === 0) return;
		setFormAthleteIds((prev) => {
			const allIn = memberIds.every((id) => prev.includes(id));
			if (allIn) return prev.filter((id) => !memberIds.includes(id));
			return [...prev, ...memberIds.filter((id) => !prev.includes(id))];
		});
	};

	const saveDialog = async () => {
		if (dialogMode !== "addAthletes" && (!form.name.trim() || !form.date)) {
			toast.error(t("fillRequiredFields"));
			return;
		}
		if (dialogMode === "create" && formAthleteIds.length === 0) {
			toast.error(t("competitionHubSelectAthletes"));
			return;
		}
		if (dialogMode === "addAthletes") {
			const newIds = formAthleteIds.filter(
				(id) => !groupMemberIds.includes(id),
			);
			if (newIds.length === 0) {
				toast.error(t("competitionHubSelectAthletesToAdd"));
				return;
			}
		}
		setSaving(true);
		try {
			if (dialogMode === "create") {
				await fetchAPI("/coach/competitions/bulk", {
					method: "POST",
					body: JSON.stringify({ ...form, athlete_ids: formAthleteIds }),
				});
				toast.success(t("competitionHubEventsCreated"));
			} else if (dialogMode === "addAthletes") {
				const response = await fetchAPI(
					`/coach/competitions/${editingEventId}/participants`,
					{
						method: "POST",
						body: JSON.stringify({
							athlete_ids: formAthleteIds.filter(
								(id) => !groupMemberIds.includes(id),
							),
						}),
					},
				);
				toast.success(
					`${t("competitionHubAthletesAdded")} (${response?.created ?? 0})`,
				);
			} else {
				const query = applyToGroup ? "?apply_to_group=true" : "";
				await fetchAPI(`/coach/competitions/${editingEventId}${query}`, {
					method: "PUT",
					body: JSON.stringify(form),
				});
				toast.success(
					applyToGroup ? t("competitionHubGroupUpdated") : t("eventUpdated"),
				);
			}
			setDialogOpen(false);
			await loadData();
		} catch {
			toast.error(t("error"));
		} finally {
			setSaving(false);
		}
	};

	const deleteEvent = async (eventId) => {
		if (!window.confirm(t("competitionHubDeleteConfirm"))) return;
		try {
			await fetchAPI(`/coach/competitions/${eventId}`, { method: "DELETE" });
			toast.success(t("eventDeleted"));
			setSelectedEventIds((prev) => prev.filter((id) => id !== eventId));
			await loadData();
		} catch {
			toast.error(t("error"));
		}
	};

	// Grouped view works on the whole cluster: deleting it removes the race for
	// every athlete shown in the card, not just the first one.
	const deleteGroup = async (group) => {
		const eventIds = (group.athletes || [])
			.map((event) => event.event_id)
			.filter(Boolean);
		if (eventIds.length === 0) return;
		if (!window.confirm(t("competitionHubDeleteConfirm"))) return;
		try {
			await fetchAPI("/coach/competitions/bulk-action", {
				method: "POST",
				body: JSON.stringify({ event_ids: eventIds, action: "delete" }),
			});
			toast.success(t("eventDeleted"));
			setSelectedEventIds((prev) =>
				prev.filter((id) => !eventIds.includes(id)),
			);
			await loadData();
		} catch {
			toast.error(t("error"));
		}
	};

	const toggleEventSelection = (eventId) => {
		setSelectedEventIds((prev) =>
			prev.includes(eventId)
				? prev.filter((id) => id !== eventId)
				: [...prev, eventId],
		);
	};

	const runBulkAction = async (action, statusValue) => {
		if (selectedEventIds.length === 0) return;
		if (
			action === "delete" &&
			!window.confirm(t("competitionHubDeleteConfirm"))
		)
			return;
		try {
			await fetchAPI("/coach/competitions/bulk-action", {
				method: "POST",
				body: JSON.stringify({
					event_ids: selectedEventIds,
					action,
					status: statusValue,
				}),
			});
			toast.success(t("competitionHubBulkDone"));
			setSelectedEventIds([]);
			setBulkStatus("");
			await loadData();
		} catch {
			toast.error(t("error"));
		}
	};

	const exportCsv = () => {
		const header = [
			"name",
			"date",
			"end_date",
			"athlete",
			"event_type",
			"discipline",
			"priority",
			"category",
			"status",
			"location",
			"notes",
		];
		const rows = filteredEvents.map((event) =>
			[
				event.name,
				event.date,
				event.end_date,
				event.athlete_name,
				event.event_type,
				event.discipline,
				event.priority,
				event.category,
				event.status,
				event.location,
				event.notes,
			]
				.map(csvCell)
				.join(","),
		);
		downloadBlob(
			[header.join(","), ...rows].join("\n"),
			"calendario-gare.csv",
			"text/csv;charset=utf-8",
		);
	};

	const exportIcs = () => {
		const stamp = new Date()
			.toISOString()
			.replace(/[-:]/g, "")
			.replace(/\.\d+Z$/, "Z");
		const lines = [
			"BEGIN:VCALENDAR",
			"VERSION:2.0",
			"PRODID:-//Kraveon//Competition Hub//IT",
		];
		filteredEvents.forEach((event) => {
			const start = (event.date || "").replace(/-/g, "");
			if (!start) return;
			const endSource = event.end_date || event.date;
			const endDate = new Date(endSource);
			endDate.setDate(endDate.getDate() + 1);
			const end = isoFromYmd(
				endDate.getFullYear(),
				endDate.getMonth(),
				endDate.getDate(),
			).replace(/-/g, "");
			lines.push(
				"BEGIN:VEVENT",
				`UID:${event.event_id}@kraveon`,
				`DTSTAMP:${stamp}`,
				`DTSTART;VALUE=DATE:${start}`,
				`DTEND;VALUE=DATE:${end}`,
				`SUMMARY:${(event.name || "").replace(/\n/g, " ")} - ${event.athlete_name || ""}`,
				`LOCATION:${(event.location || "").replace(/\n/g, " ")}`,
				"END:VEVENT",
			);
		});
		lines.push("END:VCALENDAR");
		downloadBlob(
			lines.join("\r\n"),
			"calendario-gare.ics",
			"text/calendar;charset=utf-8",
		);
	};

	const filteredAthletePickerList = athletes.filter((athlete) =>
		matchesCoachAthleteSearch(athlete, athleteSearch),
	);

	// Roster behind the "N athletes" pill. Fetched per race instead of derived
	// from `events` so the dialog always shows the full visible cluster, even when
	// the current filters hide part of it.
	const openParticipants = async (event) => {
		if (!event?.event_id) return;
		setParticipantsEvent(event);
		setParticipants([]);
		setParticipantsLoading(true);
		try {
			const data = await fetchAPI(
				`/coach/competitions/${event.event_id}/participants`,
			);
			setParticipants(data?.athletes || []);
		} catch {
			toast.error(t("competitionParticipantsError"));
		} finally {
			setParticipantsLoading(false);
		}
	};

	const renderAvatar = (event, size = "h-7 w-7") => (
		<Avatar className={`${size} border border-border`}>
			{event.athlete_picture ? (
				<AvatarImage
					src={getImageUrl(event.athlete_picture)}
					alt={event.athlete_name}
				/>
			) : null}
			<AvatarFallback className="text-[10px] bg-secondary">
				{initials(event.athlete_name)}
			</AvatarFallback>
		</Avatar>
	);

	return (
		<div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
			{/* Header */}
			<div className="flex items-start justify-between gap-4 flex-wrap">
				<div>
					<h1 className="font-heading text-2xl uppercase tracking-wide flex items-center gap-2">
						<Trophy className="w-6 h-6 text-volt" />
						{t("competitionHub")}
					</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{t("competitionHubSubtitle")}
					</p>
					{isAdmin && (
						<div className="flex items-center gap-2 mt-3">
							<Button
								size="sm"
								variant={scope === "mine" ? "default" : "outline"}
								className="rounded-sm"
								onClick={() => setScope("mine")}
							>
								{t("competitionHubScopeMine")}
							</Button>
							<Button
								size="sm"
								variant={scope === "all" ? "default" : "outline"}
								className="rounded-sm"
								onClick={() => setScope("all")}
							>
								{t("competitionHubScopeAll")}
							</Button>
						</div>
					)}
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					<Button
						variant="outline"
						className="rounded-sm"
						onClick={exportCsv}
						disabled={filteredEvents.length === 0}
					>
						<Download className="w-4 h-4 mr-1.5" />
						CSV
					</Button>
					<Button
						variant="outline"
						className="rounded-sm"
						onClick={exportIcs}
						disabled={filteredEvents.length === 0}
					>
						<Download className="w-4 h-4 mr-1.5" />
						ICS
					</Button>
					<Button
						className="bg-volt hover:bg-volt/90 rounded-sm"
						onClick={openCreateDialog}
					>
						<Plus className="w-4 h-4 mr-1.5" />
						{t("competitionHubNewRace")}
					</Button>
				</div>
			</div>

			{/* Conflict / gap detection */}
			{conflicts.length > 0 && (
				<Card className="bg-blaze/10 border-blaze/40 rounded-sm">
					<CardContent className="p-3">
						<div className="flex items-center gap-2 text-blaze mb-1">
							<AlertTriangle className="w-4 h-4" />
							<span className="text-xs uppercase tracking-wide font-heading">
								{t("competitionHubConflicts")}
							</span>
						</div>
						<div className="space-y-1">
							{conflicts.slice(0, 4).map((conflict) => (
								<p
									key={`${conflict.a.event_id}-${conflict.b.event_id}`}
									className="text-xs text-muted-foreground"
								>
									<span className="text-foreground">
										{conflict.athlete_name}
									</span>
									{": "}
									{conflict.a.name} → {conflict.b.name}{" "}
									<span className="text-blaze">
										({conflict.gap}
										{t("competitionHubDaysShort")})
									</span>
								</p>
							))}
							{conflicts.length > 4 && (
								<p className="text-xs text-muted-foreground">
									+{conflicts.length - 4}
								</p>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* KPI dashboard */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-4">
						<div className="flex items-center gap-2 text-muted-foreground">
							<Flame className="w-4 h-4 text-blaze" />
							<span className="text-[11px] uppercase tracking-wide">
								{t("competitionHubKpiNext30")}
							</span>
						</div>
						<p className="font-heading text-3xl mt-1">{kpis.next30}</p>
					</CardContent>
				</Card>
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-4">
						<div className="flex items-center gap-2 text-muted-foreground">
							<CalendarDays className="w-4 h-4 text-volt" />
							<span className="text-[11px] uppercase tracking-wide">
								{t("competitionHubKpiNextRace")}
							</span>
						</div>
						{kpis.nextRace ? (
							<>
								<p className="font-heading text-sm mt-1 truncate">
									{kpis.nextRace.name}
								</p>
								<p className="text-xs text-volt">
									{kpis.nextRaceDelta === 0
										? t("competitionHubToday")
										: `${t("competitionHubIn")} ${kpis.nextRaceDelta}${t(
												"competitionHubDaysShort",
											)}`}
								</p>
							</>
						) : (
							<p className="font-heading text-lg mt-1 text-muted-foreground">
								{t("competitionHubNone")}
							</p>
						)}
					</CardContent>
				</Card>
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-4">
						<div className="flex items-center gap-2 text-muted-foreground">
							<Users className="w-4 h-4 text-acid" />
							<span className="text-[11px] uppercase tracking-wide">
								{t("competitionHubKpiShared")}
							</span>
						</div>
						<p className="font-heading text-3xl mt-1 text-acid">
							{kpis.sharedCount}
						</p>
					</CardContent>
				</Card>
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-4">
						<div className="flex items-center gap-2 text-muted-foreground">
							<Users className="w-4 h-4" />
							<span className="text-[11px] uppercase tracking-wide">
								{t("competitionHubKpiWithoutRace")}
							</span>
						</div>
						<p className="font-heading text-3xl mt-1">{kpis.withoutRace}</p>
					</CardContent>
				</Card>
			</div>

			{/* Filters */}
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-4 space-y-4">
					<div className="flex items-center justify-between gap-2">
						<span className="text-[11px] uppercase tracking-widest text-muted-foreground font-heading">
							{t("competitionHubFilters")}
						</span>
						<Popover>
							<PopoverTrigger asChild>
								<button
									type="button"
									className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
								>
									<Info className="w-3.5 h-3.5" />
									{t("competitionHubLegend")}
								</button>
							</PopoverTrigger>
							<PopoverContent
								className="w-80 bg-card border-border text-sm"
								align="end"
							>
								<p className="font-heading text-xs uppercase tracking-wide mb-2">
									{t("competitionHubLegendTypeTitle")}
								</p>
								<p className="text-xs text-muted-foreground mb-3">
									{t("competitionHubLegendTypeBody")}
								</p>
								<p className="font-heading text-xs uppercase tracking-wide mb-1">
									{t("competitionPriority")}
								</p>
								<ul className="text-xs text-muted-foreground space-y-0.5 mb-3">
									<li>
										<span className="text-blaze font-heading">A</span> —{" "}
										{t("competitionHubLegendPriorityA")}
									</li>
									<li>
										<span className="text-volt font-heading">B</span> —{" "}
										{t("competitionHubLegendPriorityB")}
									</li>
									<li>
										<span className="font-heading">C</span> —{" "}
										{t("competitionHubLegendPriorityC")}
									</li>
								</ul>
								<p className="font-heading text-xs uppercase tracking-wide mb-1">
									{t("competitionStatus")}
								</p>
								<p className="text-xs text-muted-foreground mb-2">
									{t("competitionHubLegendStatusIntro")}
								</p>
								<ul className="text-xs text-muted-foreground space-y-0.5">
									<li>
										<span className="text-foreground">
											{t("competitionStatusPlanned")}
										</span>{" "}
										— {t("competitionHubLegendStatusPlanned")}
									</li>
									<li>
										<span className="text-foreground">
											{t("competitionStatusConfirmed")}
										</span>{" "}
										— {t("competitionHubLegendStatusConfirmed")}
									</li>
									<li>
										<span className="text-foreground">
											{t("competitionStatusRegistered")}
										</span>{" "}
										— {t("competitionHubLegendStatusRegistered")}
									</li>
									<li>
										<span className="text-foreground">
											{t("competitionStatusDone")}
										</span>{" "}
										— {t("competitionHubLegendStatusDone")}
									</li>
									<li>
										<span className="text-foreground">
											{t("competitionStatusCancelled")}
										</span>{" "}
										— {t("competitionHubLegendStatusCancelled")}
									</li>
								</ul>
							</PopoverContent>
						</Popover>
					</div>
					<div className="flex flex-col md:flex-row gap-3 md:items-center">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
							<Input
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder={t("competitionHubSearchPlaceholder")}
								className="pl-9 rounded-sm"
							/>
						</div>
						<Select value={period} onValueChange={setPeriod}>
							<SelectTrigger className="w-full md:w-48 rounded-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="upcoming">
									{t("competitionHubPeriodUpcoming")}
								</SelectItem>
								<SelectItem value="next30">
									{t("competitionHubPeriodNext30")}
								</SelectItem>
								<SelectItem value="next90">
									{t("competitionHubPeriodNext90")}
								</SelectItem>
								<SelectItem value="past">
									{t("competitionHubPeriodPast")}
								</SelectItem>
								<SelectItem value="all">
									{t("competitionHubPeriodAll")}
								</SelectItem>
							</SelectContent>
						</Select>
						<Popover
							open={athletePickerOpen}
							onOpenChange={setAthletePickerOpen}
						>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className="w-full md:w-52 justify-between rounded-sm"
								>
									<span className="flex items-center gap-2 truncate">
										<Users className="w-4 h-4" />
										{selectedAthleteIds.length > 0
											? `${selectedAthleteIds.length} ${t(
													"competitionHubAthletesSelected",
												)}`
											: t("competitionHubAllAthletes")}
									</span>
									<ChevronDown className="w-4 h-4 opacity-60" />
								</Button>
							</PopoverTrigger>
							<PopoverContent
								className="w-64 p-2 bg-card border-border"
								align="end"
							>
								<Input
									value={athleteSearch}
									onChange={(event) => setAthleteSearch(event.target.value)}
									placeholder={t("searchAthletes")}
									className="mb-2 rounded-sm h-8"
								/>
								<ScrollArea className="h-52 pr-2">
									<div className="space-y-1">
										{filteredAthletePickerList.map((athlete) => {
											const checked = selectedAthleteIds.includes(
												athlete.user_id,
											);
											return (
												<button
													key={athlete.user_id}
													type="button"
													onClick={() => toggleAthlete(athlete.user_id)}
													className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-sm text-sm ${
														checked
															? "bg-volt/15 text-volt"
															: "hover:bg-secondary"
													}`}
												>
													<Avatar className="h-6 w-6 border border-border">
														{athlete.picture ? (
															<AvatarImage
																src={getImageUrl(athlete.picture)}
																alt={athlete.name}
															/>
														) : null}
														<AvatarFallback className="text-[9px] bg-secondary">
															{initials(athlete.name)}
														</AvatarFallback>
													</Avatar>
													<span className="min-w-0">
														<span className="block truncate">
															{athlete.name}
														</span>
														{athlete.email && (
															<span className="block truncate text-xs text-muted-foreground">
																{athlete.email}
															</span>
														)}
													</span>
												</button>
											);
										})}
										{filteredAthletePickerList.length === 0 && (
											<p className="text-xs text-muted-foreground px-2 py-1.5">
												{t("noAthletesFound")}
											</p>
										)}
									</div>
								</ScrollArea>
							</PopoverContent>
						</Popover>
					</div>

					<div className="flex flex-wrap items-end gap-x-6 gap-y-3">
						<div>
							<label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
								{t("competitionHubFilterType")}
							</label>
							<div className="flex flex-wrap items-center gap-2">
								{EVENT_TYPES.map((type) => {
									const active = selectedTypes.includes(type);
									return (
										<button
											key={type}
											type="button"
											onClick={() => toggleType(type)}
											className={`text-xs uppercase tracking-wide px-2.5 py-1 rounded-sm border transition-colors ${
												active
													? `${eventTypeClass(type)} border-transparent`
													: "border-border text-muted-foreground hover:text-foreground"
											}`}
										>
											{t(`eventType_${type}`)}
										</button>
									);
								})}
							</div>
						</div>
						<div>
							<label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
								{t("competitionPriority")}
							</label>
							<div className="flex flex-wrap items-center gap-2">
								{PRIORITIES.map((priority) => {
									const active = selectedPriorities.includes(priority);
									return (
										<button
											key={priority}
											type="button"
											onClick={() => togglePriority(priority)}
											className={`text-xs uppercase tracking-wide px-2.5 py-1 rounded-sm border transition-colors ${
												active
													? `${priorityClass(priority)} border-transparent`
													: "border-border text-muted-foreground hover:text-foreground"
											}`}
											title={t(`competitionPriority${priority}`)}
										>
											{priority}
										</button>
									);
								})}
							</div>
						</div>
						<div>
							<label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
								{t("competitionHubFilterOther")}
							</label>
							<div className="flex flex-wrap items-center gap-2">
								<button
									type="button"
									onClick={() => setSharedOnly((prev) => !prev)}
									className={`text-xs uppercase tracking-wide px-2.5 py-1 rounded-sm border transition-colors flex items-center gap-1 ${
										sharedOnly
											? "bg-acid/20 text-acid border-transparent"
											: "border-border text-muted-foreground hover:text-foreground"
									}`}
								>
									<Users className="w-3 h-3" />
									{t("competitionHubSharedOnly")}
								</button>
								{activeFilterCount > 0 && (
									<button
										type="button"
										onClick={resetFilters}
										className="text-xs uppercase tracking-wide px-2.5 py-1 rounded-sm text-muted-foreground hover:text-blaze flex items-center gap-1"
									>
										<X className="w-3 h-3" />
										{t("competitionHubResetFilters")}
									</button>
								)}
							</div>
						</div>
					</div>

					<div className="flex flex-wrap items-end gap-x-6 gap-y-3">
						{disciplineOptions.length > 0 && (
							<div>
								<label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
									{t("competitionSport")}
								</label>
								<Select
									value={selectedDiscipline}
									onValueChange={setSelectedDiscipline}
								>
									<SelectTrigger
										className="w-full sm:w-52 rounded-sm h-9"
										aria-label={t("competitionSport")}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="__all__">
											{t("competitionHubAllSports")}
										</SelectItem>
										{disciplineOptions.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.linked
													? option.label
													: `${option.label} · ${t("competitionHubFreeText")}`}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
						<div>
							<label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
								{t("competitionStatus")}
							</label>
							<Select value={selectedStatus} onValueChange={setSelectedStatus}>
								<SelectTrigger className="w-full sm:w-52 rounded-sm h-9">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__all__">
										{t("competitionHubAllStatus")}
									</SelectItem>
									{STATUSES.map((status) => (
										<SelectItem key={status} value={status}>
											{t(STATUS_LABEL_KEYS[status])}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* View toggle */}
			<Tabs value={view} onValueChange={setView}>
				<TabsList className="rounded-sm flex-wrap h-auto">
					<TabsTrigger value="list" className="rounded-sm">
						<Filter className="w-4 h-4 mr-1.5" />
						{t("competitionHubViewList")}
					</TabsTrigger>
					<TabsTrigger value="shared" className="rounded-sm">
						<Users className="w-4 h-4 mr-1.5" />
						{t("competitionHubViewShared")}
					</TabsTrigger>
					<TabsTrigger value="calendar" className="rounded-sm">
						<LayoutGrid className="w-4 h-4 mr-1.5" />
						{t("competitionHubViewCalendar")}
					</TabsTrigger>
					<TabsTrigger value="timeline" className="rounded-sm">
						<CalendarRange className="w-4 h-4 mr-1.5" />
						{t("competitionHubViewTimeline")}
					</TabsTrigger>
				</TabsList>
			</Tabs>

			{/* Bulk action bar */}
			{selectedEventIds.length > 0 && (
				<Card className="bg-volt/10 border-volt/40 rounded-sm">
					<CardContent className="p-3 flex items-center gap-3 flex-wrap">
						<span className="text-sm font-heading">
							{selectedEventIds.length} {t("competitionHubSelected")}
						</span>
						<div className="flex items-center gap-2 flex-wrap ml-auto">
							<Select
								value={bulkStatus}
								onValueChange={(value) => {
									setBulkStatus(value);
									runBulkAction("set_status", value);
								}}
							>
								<SelectTrigger className="w-44 rounded-sm h-9">
									<SelectValue placeholder={t("competitionHubSetStatus")} />
								</SelectTrigger>
								<SelectContent>
									{STATUSES.map((status) => (
										<SelectItem key={status} value={status}>
											{t(STATUS_LABEL_KEYS[status])}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Button
								variant="outline"
								className="rounded-sm text-blaze hover:text-blaze"
								onClick={() => runBulkAction("delete")}
							>
								<Trash2 className="w-4 h-4 mr-1.5" />
								{t("delete")}
							</Button>
							<Button
								variant="ghost"
								className="rounded-sm"
								onClick={() => setSelectedEventIds([])}
							>
								<X className="w-4 h-4 mr-1.5" />
								{t("competitionHubClearSelection")}
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Content */}
			{loading ? (
				<div className="flex items-center justify-center py-16 text-muted-foreground">
					<Loader2 className="w-5 h-5 animate-spin mr-2" />
					{t("loading")}
				</div>
			) : view === "calendar" ? (
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<button
							type="button"
							onClick={() => shiftMonth(-1)}
							className="p-2 rounded-sm hover:bg-secondary text-muted-foreground"
							aria-label={t("competitionHubPrevMonth")}
						>
							<ChevronLeft className="w-4 h-4" />
						</button>
						<h2 className="font-heading text-sm uppercase tracking-widest">
							{monthLabel(ymKey(calendarMonth))}
						</h2>
						<button
							type="button"
							onClick={() => shiftMonth(1)}
							className="p-2 rounded-sm hover:bg-secondary text-muted-foreground"
							aria-label={t("competitionHubNextMonth")}
						>
							<ChevronRight className="w-4 h-4" />
						</button>
					</div>
					<Card className="bg-card border-border rounded-sm">
						<CardContent className="p-2 sm:p-3">
							<div className="grid grid-cols-7 gap-1">
								{WEEKDAYS_IT.map((weekday) => (
									<div
										key={weekday}
										className="text-center text-[10px] uppercase tracking-wide text-muted-foreground py-1"
									>
										{weekday}
									</div>
								))}
								{calendarGrid.map((cell, index) =>
									cell === null ? (
										<div key={`empty-${index}`} />
									) : (
										<button
											key={cell.iso}
											type="button"
											onClick={() =>
												cell.events.length > 0 &&
												setSelectedCalendarDay((prev) =>
													prev === cell.iso ? null : cell.iso,
												)
											}
											className={`min-h-[64px] sm:min-h-[80px] border rounded-sm p-1 text-left flex flex-col ${
												cell.iso === today ? "border-volt" : "border-border"
											} ${
												selectedCalendarDay === cell.iso
													? "bg-volt/10"
													: cell.events.length
														? "hover:bg-secondary"
														: ""
											}`}
										>
											<span
												className={`text-[11px] ${
													cell.iso === today
														? "text-volt font-heading"
														: "text-muted-foreground"
												}`}
											>
												{cell.day}
											</span>
											<div className="mt-0.5 space-y-0.5 overflow-hidden">
												{cell.events.slice(0, 3).map((ev) => (
													<div
														key={ev.event_id}
														title={`${ev.name} · ${ev.athlete_name}`}
														className={`text-[9px] leading-tight px-1 py-0.5 rounded-sm truncate ${eventTypeClass(
															ev.event_type || "altro",
														)}`}
													>
														{ev.name}
													</div>
												))}
												{cell.events.length > 3 && (
													<div className="text-[9px] text-muted-foreground px-1">
														+{cell.events.length - 3}
													</div>
												)}
											</div>
										</button>
									),
								)}
							</div>
						</CardContent>
					</Card>
					{selectedCalendarDay && selectedDayEvents.length > 0 && (
						<Card className="bg-card border-border rounded-sm">
							<CardContent className="p-3">
								<p className="font-heading text-sm uppercase tracking-wide mb-2">
									{shortDate(selectedCalendarDay)}
								</p>
								<div className="space-y-2">
									{selectedDayEvents.map((ev) => (
										<div key={ev.event_id} className="flex items-center gap-2">
											{renderAvatar(ev, "h-6 w-6")}
											<div className="min-w-0 flex-1">
												<p className="text-sm font-heading truncate">
													{ev.name}
												</p>
												<p className="text-xs text-muted-foreground truncate">
													{ev.athlete_name}
													{ev.location ? ` · ${ev.location}` : ""}
												</p>
											</div>
											<Badge
												className={`rounded-sm border-0 text-[10px] uppercase ${eventTypeClass(
													ev.event_type || "altro",
												)}`}
											>
												{t(`eventType_${ev.event_type || "altro"}`)}
											</Badge>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					)}
					{scopedEvents.length === 0 && (
						<p className="text-sm text-muted-foreground text-center py-4">
							{t("competitionHubEmpty")}
						</p>
					)}
				</div>
			) : view === "timeline" ? (
				timeline.lanes.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 text-center">
						<CalendarRange className="w-10 h-10 text-muted-foreground mb-3" />
						<p className="text-sm text-muted-foreground">
							{t("competitionHubEmpty")}
						</p>
					</div>
				) : (
					<Card className="bg-card border-border rounded-sm">
						<CardContent className="p-3 overflow-x-auto">
							<div
								style={{
									minWidth: `${180 + timeline.months.length * 96}px`,
								}}
							>
								<div
									className="grid"
									style={{
										gridTemplateColumns: `180px repeat(${timeline.months.length}, minmax(88px, 1fr))`,
									}}
								>
									<div />
									{timeline.months.map((month) => (
										<div
											key={month}
											className="text-center text-[10px] uppercase tracking-wide text-muted-foreground py-1 border-l border-border"
										>
											{monthShortLabel(month)}
										</div>
									))}
								</div>
								<div className="space-y-1 mt-1">
									{timeline.lanes.map((lane) => (
										<div
											key={lane.athlete_id}
											className="grid items-stretch"
											style={{
												gridTemplateColumns: `180px repeat(${timeline.months.length}, minmax(88px, 1fr))`,
											}}
										>
											<div className="flex items-center gap-2 pr-2 min-w-0">
												<Avatar className="h-6 w-6 border border-border">
													{lane.picture ? (
														<AvatarImage
															src={getImageUrl(lane.picture)}
															alt={lane.name}
														/>
													) : null}
													<AvatarFallback className="text-[9px] bg-secondary">
														{initials(lane.name)}
													</AvatarFallback>
												</Avatar>
												<span className="text-xs truncate">{lane.name}</span>
											</div>
											{timeline.months.map((month) => {
												const monthEvents = lane.byMonth.get(month) || [];
												return (
													<div
														key={month}
														className="border-l border-border min-h-[36px] px-1 py-1 flex flex-wrap gap-1 content-start"
													>
														{monthEvents.map((ev) => (
															<span
																key={ev.event_id}
																title={`${ev.name} · ${shortDate(ev.date)}`}
																className={`text-[9px] leading-none px-1 py-0.5 rounded-sm ${eventTypeClass(
																	ev.event_type || "altro",
																)}`}
															>
																{Number(ev.date.slice(8, 10))}
															</span>
														))}
													</div>
												);
											})}
										</div>
									))}
								</div>
							</div>
						</CardContent>
					</Card>
				)
			) : filteredEvents.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<CalendarDays className="w-10 h-10 text-muted-foreground mb-3" />
					<p className="text-sm text-muted-foreground">
						{t("competitionHubEmpty")}
					</p>
				</div>
			) : view === "list" ? (
				<div className="space-y-6">
					{eventsByMonth.map(([month, list]) => (
						<div key={month}>
							<h2 className="font-heading text-sm uppercase tracking-widest text-muted-foreground mb-2">
								{month === "—" ? "—" : monthLabel(month)}
							</h2>
							<div className="space-y-2">
								{list.map((event) => {
									const delta = daysUntil(event.date, today);
									const sharedCount =
										sharedCounts.get(event.shared_group_key) || 1;
									return (
										<Card
											key={event.event_id}
											className="bg-card border-border rounded-sm"
										>
											<CardContent className="p-3 flex items-center gap-3">
												<button
													type="button"
													onClick={() => toggleEventSelection(event.event_id)}
													className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
														selectedEventIds.includes(event.event_id)
															? "bg-volt border-volt text-white"
															: "border-border text-transparent hover:border-volt"
													}`}
													aria-label={t("select")}
												>
													<Check className="w-3 h-3" />
												</button>
												{renderAvatar(event)}
												<div className="min-w-0 flex-1">
													<div className="flex items-center gap-2 flex-wrap">
														<p className="font-heading text-sm truncate">
															{event.name}
														</p>
														{event.priority && (
															<Badge
																className={`rounded-sm border-0 text-[10px] ${priorityClass(
																	event.priority,
																)}`}
															>
																{event.priority}
															</Badge>
														)}
														<Badge
															className={`rounded-sm border-0 text-[10px] uppercase ${eventTypeClass(
																event.event_type || "altro",
															)}`}
														>
															{t(`eventType_${event.event_type || "altro"}`)}
														</Badge>
														{sharedCount >= 2 && (
															<SharedAthletesBadge
																count={sharedCount}
																onClick={() => openParticipants(event)}
															/>
														)}
													</div>
													<div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
														<span className="truncate">
															{event.athlete_name}
														</span>
														{event.discipline && (
															<span className="truncate">
																{event.discipline}
															</span>
														)}
														{event.location && (
															<span className="flex items-center gap-1">
																<MapPin className="w-3 h-3" />
																{event.location}
															</span>
														)}
													</div>
												</div>
												<div className="text-right shrink-0">
													<p className="font-heading text-sm">
														{shortDate(event.date)}
													</p>
													{delta !== null && delta >= 0 && (
														<p
															className={`text-[11px] ${
																delta <= 14 ? "text-blaze" : "text-volt"
															}`}
														>
															{delta === 0
																? t("competitionHubToday")
																: `${delta}${t("competitionHubDaysShort")}`}
														</p>
													)}
												</div>
												<div className="flex items-center gap-1 shrink-0">
													<button
														type="button"
														onClick={() => openAddAthletesDialog(event)}
														className="p-1.5 rounded-sm text-muted-foreground hover:text-acid hover:bg-secondary"
														aria-label={t("competitionHubAddAthletes")}
														title={t("competitionHubAddAthletes")}
													>
														<UserPlus className="w-3.5 h-3.5" />
													</button>
													<button
														type="button"
														onClick={() => openEditDialog(event)}
														className="p-1.5 rounded-sm text-muted-foreground hover:text-volt hover:bg-secondary"
														aria-label={t("edit")}
													>
														<Pencil className="w-3.5 h-3.5" />
													</button>
													<button
														type="button"
														onClick={() => deleteEvent(event.event_id)}
														className="p-1.5 rounded-sm text-muted-foreground hover:text-blaze hover:bg-secondary"
														aria-label={t("delete")}
													>
														<Trash2 className="w-3.5 h-3.5" />
													</button>
												</div>
											</CardContent>
										</Card>
									);
								})}
							</div>
						</div>
					))}
				</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
					{sharedGroups.map((group) => (
						<Card key={group.key} className="bg-card border-border rounded-sm">
							<CardContent className="p-4">
								<div className="flex items-start justify-between gap-2">
									<div className="min-w-0">
										<p className="font-heading text-sm truncate">
											{group.name}
										</p>
										<div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
											<span className="flex items-center gap-1">
												<CalendarDays className="w-3 h-3" />
												{shortDate(group.date)}
											</span>
											{group.location && (
												<span className="flex items-center gap-1">
													<MapPin className="w-3 h-3" />
													{group.location}
												</span>
											)}
										</div>
									</div>
									<SharedAthletesBadge
										count={group.athletes.length}
										onClick={() => openParticipants(group.athletes[0])}
										className={
											group.athletes.length >= 2
												? ""
												: `uppercase ${eventTypeClass(group.event_type || "altro")}`
										}
									/>
								</div>
								<div className="mt-3 space-y-1.5">
									{group.athletes.map((event) => (
										<div
											key={event.event_id}
											className="flex items-center gap-2 text-sm"
										>
											{renderAvatar(event, "h-6 w-6")}
											<span className="truncate">{event.athlete_name}</span>
										</div>
									))}
								</div>
								<div className="mt-3 flex items-center gap-2">
									<Button
										variant="outline"
										size="sm"
										className="rounded-sm h-8 text-xs"
										onClick={() => openAddAthletesDialog(group.athletes[0])}
									>
										<UserPlus className="w-3.5 h-3.5 mr-1.5" />
										{t("competitionHubAddAthletes")}
									</Button>
									<Button
										variant="ghost"
										size="sm"
										className="rounded-sm h-8 text-xs"
										onClick={() => openEditDialog(group.athletes[0])}
									>
										<Pencil className="w-3.5 h-3.5 mr-1.5" />
										{t("edit")}
									</Button>
									<Button
										variant="ghost"
										size="sm"
										className="rounded-sm h-8 text-xs text-muted-foreground hover:text-blaze"
										onClick={() => deleteGroup(group)}
									>
										<Trash2 className="w-3.5 h-3.5 mr-1.5" />
										{t("delete")}
									</Button>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			)}

			<CompetitionParticipantsDialog
				open={Boolean(participantsEvent)}
				onOpenChange={(open) => {
					if (!open) setParticipantsEvent(null);
				}}
				event={participantsEvent}
				athletes={participants}
				loading={participantsLoading}
			/>

			{/* Create / edit dialog */}
			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{dialogMode === "edit"
								? t("editEvent")
								: dialogMode === "addAthletes"
									? t("competitionHubAddAthletesTitle")
									: t("competitionHubNewRace")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						{dialogMode === "addAthletes" && (
							<div className="rounded-sm border border-border bg-background p-3">
								<p className="font-heading text-sm truncate">{form.name}</p>
								<div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
									<span className="flex items-center gap-1">
										<CalendarDays className="w-3 h-3" />
										{shortDate(form.date)}
									</span>
									{form.location && (
										<span className="flex items-center gap-1">
											<MapPin className="w-3 h-3" />
											{form.location}
										</span>
									)}
									<span className="flex items-center gap-1">
										<Users className="w-3 h-3" />
										{groupMemberIds.length} {t("competitionHubParticipants")}
									</span>
								</div>
							</div>
						)}

						{dialogMode !== "addAthletes" && (
							<div>
								<label className="label-tactical mb-1 block">{t("name")}</label>
								<Input
									className="bg-background border-border rounded-sm"
									value={form.name}
									onChange={(e) =>
										setForm((prev) => ({ ...prev, name: e.target.value }))
									}
								/>
								{nameSuggestions.length > 0 && (
									<div className="mt-2 rounded-sm border border-border bg-background p-2 space-y-1">
										<p className="text-[10px] uppercase tracking-widest text-muted-foreground px-1">
											{t("competitionHubExistingRace")}
										</p>
										{nameSuggestions.map((suggestion) => (
											<div
												key={suggestion.key}
												className="w-full px-2 py-1.5 rounded-sm hover:bg-secondary flex items-center gap-2"
											>
												<button
													type="button"
													onClick={() =>
														openAddAthletesDialog(suggestion.anchor)
													}
													className="min-w-0 flex-1 text-left"
												>
													<p className="text-sm truncate">
														{suggestion.anchor.name}
													</p>
													<p className="text-xs text-muted-foreground truncate">
														{shortDate(suggestion.anchor.date)}
														{suggestion.anchor.location
															? ` · ${suggestion.anchor.location}`
															: ""}
													</p>
												</button>
												<SharedAthletesBadge
													count={suggestion.count}
													onClick={() => openParticipants(suggestion.anchor)}
												/>
											</div>
										))}
									</div>
								)}
							</div>
						)}

						{dialogMode !== "addAthletes" && (
							<>
								<div className="grid grid-cols-2 gap-3">
									<div>
										<label className="label-tactical mb-1 block">
											{t("date")}
										</label>
										<Input
											type="date"
											className="bg-background border-border rounded-sm"
											value={form.date}
											onChange={(e) =>
												setForm((prev) => ({ ...prev, date: e.target.value }))
											}
										/>
									</div>
									<div>
										<label className="label-tactical mb-1 block">
											{t("competitionEndDate")}
										</label>
										<Input
											type="date"
											className="bg-background border-border rounded-sm"
											value={form.end_date}
											onChange={(e) =>
												setForm((prev) => ({
													...prev,
													end_date: e.target.value,
												}))
											}
										/>
									</div>
								</div>
								<div className="grid grid-cols-2 gap-3">
									<div>
										<label className="label-tactical mb-1 block">
											{t("type")}
										</label>
										<Select
											value={form.event_type || "gara"}
											onValueChange={(value) =>
												setForm((prev) => ({ ...prev, event_type: value }))
											}
										>
											<SelectTrigger className="bg-background border-border rounded-sm">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{EVENT_TYPES.map((type) => (
													<SelectItem key={type} value={type}>
														{t(`eventType_${type}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div>
										<label className="label-tactical mb-1 block">
											{t("competitionPriority")}
										</label>
										<Select
											value={form.priority || "__none__"}
											onValueChange={(value) =>
												setForm((prev) => ({
													...prev,
													priority: value === "__none__" ? "" : value,
												}))
											}
										>
											<SelectTrigger className="bg-background border-border rounded-sm">
												<SelectValue placeholder="—" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="__none__">—</SelectItem>
												{PRIORITIES.map((priority) => (
													<SelectItem key={priority} value={priority}>
														{priority}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</div>
								<div className="grid grid-cols-2 gap-3">
									<div>
										<label className="label-tactical mb-1 block">
											{t("competitionSport")}
										</label>
										<CompetitionSportField form={form} setForm={setForm} />
									</div>
									<div>
										<label className="label-tactical mb-1 block">
											{t("competitionCategory")}
										</label>
										<Input
											className="bg-background border-border rounded-sm"
											value={form.category}
											onChange={(e) =>
												setForm((prev) => ({
													...prev,
													category: e.target.value,
												}))
											}
											placeholder={t("competitionCategoryPlaceholder")}
										/>
									</div>
								</div>
								<div className="grid grid-cols-2 gap-3">
									<div>
										<label className="label-tactical mb-1 block">
											{t("competitionStatus")}
										</label>
										<Select
											value={form.status || "__none__"}
											onValueChange={(value) =>
												setForm((prev) => ({
													...prev,
													status: value === "__none__" ? "" : value,
												}))
											}
										>
											<SelectTrigger className="bg-background border-border rounded-sm">
												<SelectValue placeholder="—" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="__none__">—</SelectItem>
												{STATUSES.map((status) => (
													<SelectItem key={status} value={status}>
														{t(STATUS_LABEL_KEYS[status])}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div>
										<label className="label-tactical mb-1 block">
											{t("locationLabel")}
										</label>
										<Input
											className="bg-background border-border rounded-sm"
											value={form.location}
											onChange={(e) =>
												setForm((prev) => ({
													...prev,
													location: e.target.value,
												}))
											}
										/>
									</div>
								</div>
								<div>
									<label className="label-tactical mb-1 block">
										{t("notes")}
									</label>
									<Textarea
										className="bg-background border-border rounded-sm min-h-[70px]"
										value={form.notes}
										onChange={(e) =>
											setForm((prev) => ({ ...prev, notes: e.target.value }))
										}
									/>
								</div>
							</>
						)}

						{dialogMode === "edit" && groupMemberIds.length > 1 && (
							<button
								type="button"
								onClick={() => setApplyToGroup((prev) => !prev)}
								className="w-full text-left flex items-start gap-2 rounded-sm border border-border bg-background p-3"
							>
								<span
									className={`w-4 h-4 mt-0.5 rounded-sm border flex items-center justify-center shrink-0 ${
										applyToGroup
											? "bg-volt border-volt text-white"
											: "border-border text-transparent"
									}`}
								>
									<Check className="w-3 h-3" />
								</span>
								<span className="min-w-0">
									<span className="block text-sm">
										{t("competitionHubApplyToGroup")} (
										{groupMemberIds.length - 1})
									</span>
									<span className="block text-xs text-muted-foreground mt-0.5">
										{t("competitionHubApplyToGroupHint")}
									</span>
								</span>
							</button>
						)}

						{(dialogMode === "create" || dialogMode === "addAthletes") && (
							<div>
								<label className="label-tactical mb-1 block">
									{dialogMode === "addAthletes"
										? t("competitionHubSelectAthletesToAdd")
										: t("competitionHubAssignAthletes")}{" "}
									(
									{
										formAthleteIds.filter(
											(id) =>
												dialogMode !== "addAthletes" ||
												!groupMemberIds.includes(id),
										).length
									}
									)
								</label>
								{dialogMode === "create" && athleteGroups.length > 0 && (
									<div className="mb-2 flex flex-wrap items-center gap-1.5">
										<span className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
											<Users className="w-3 h-3" />
											{t("competitionHubPickGroup")}
										</span>
										{athleteGroups.map((group) => {
											const memberIds = (group.athlete_ids || []).filter((id) =>
												athletes.some((athlete) => athlete.user_id === id),
											);
											const allIn =
												memberIds.length > 0 &&
												memberIds.every((id) => formAthleteIds.includes(id));
											return (
												<button
													key={group.group_id}
													type="button"
													onClick={() => toggleFormGroup(group)}
													disabled={memberIds.length === 0}
													className={`h-6 px-2 rounded-sm border text-[11px] transition-colors disabled:opacity-40 ${
														allIn
															? "border-volt bg-volt/10 text-volt"
															: "border-border text-muted-foreground hover:border-volt/50"
													}`}
												>
													{group.name} · {memberIds.length}
												</button>
											);
										})}
									</div>
								)}
								<Input
									value={dialogAthleteSearch}
									onChange={(e) => setDialogAthleteSearch(e.target.value)}
									placeholder={t("searchAthletes")}
									className="mb-2 rounded-sm h-8 bg-background border-border"
								/>
								<ScrollArea className="h-44 border border-border rounded-sm p-1">
									<div className="space-y-1">
										{athletes
											.filter((athlete) =>
												matchesCoachAthleteSearch(athlete, dialogAthleteSearch),
											)
											.map((athlete) => {
												const alreadyIn =
													dialogMode === "addAthletes" &&
													groupMemberIds.includes(athlete.user_id);
												const checked =
													alreadyIn || formAthleteIds.includes(athlete.user_id);
												return (
													<button
														key={athlete.user_id}
														type="button"
														disabled={alreadyIn}
														onClick={() =>
															!alreadyIn && toggleFormAthlete(athlete.user_id)
														}
														className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-sm text-sm ${
															alreadyIn
																? "opacity-50 cursor-not-allowed"
																: checked
																	? "bg-volt/15 text-volt"
																	: "hover:bg-secondary"
														}`}
													>
														<span
															className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
																checked
																	? "bg-volt border-volt text-white"
																	: "border-border text-transparent"
															}`}
														>
															<Check className="w-3 h-3" />
														</span>
														<Avatar className="h-6 w-6 border border-border">
															{athlete.picture ? (
																<AvatarImage
																	src={getImageUrl(athlete.picture)}
																	alt={athlete.name}
																/>
															) : null}
															<AvatarFallback className="text-[9px] bg-secondary">
																{initials(athlete.name)}
															</AvatarFallback>
														</Avatar>
														<span className="min-w-0">
															<span className="block truncate">
																{athlete.name}
															</span>
															{athlete.email && (
																<span className="block truncate text-xs text-muted-foreground">
																	{athlete.email}
																</span>
															)}
														</span>
														{alreadyIn && (
															<span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
																{t("competitionHubAlreadyIn")}
															</span>
														)}
													</button>
												);
											})}
									</div>
								</ScrollArea>
							</div>
						)}

						<div className="flex justify-end gap-2 pt-2">
							<Button
								variant="ghost"
								className="rounded-sm"
								onClick={() => setDialogOpen(false)}
							>
								{t("cancel")}
							</Button>
							<Button
								className="bg-volt hover:bg-volt/90 rounded-sm"
								onClick={saveDialog}
								disabled={saving}
							>
								{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
								{t("save")}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
